/**
 * Merges pipelines based on specified rules for a merge request
 * @param {Object} mr - Merge request object containing headPipeline and pipelines
 * @returns {Object|null} - The merged pipeline or null if no valid pipeline found
 */
export const mergePipelines = (mr) => {
	if (!mr) return null;
	const { headPipeline } = mr;
	const pipelines = mr.pipelines?.nodes ?? [];

	if (!pipelines.length && !headPipeline) return null;

	// Create a combined array of all pipelines, filtering out null/undefined values
	const allPipelines = [...(headPipeline ? [headPipeline] : []), ...pipelines].filter(p => p != null);

	// Find MERGE_TRAIN pipeline - highest priority
	const mergeTrain = allPipelines.find(
		(p) => p.mergeRequestEventType === "MERGE_TRAIN",
	);
	if (mergeTrain) return mergeTrain;

	// Find MERGED_RESULT pipeline and latest pipeline
	const mergedResult = allPipelines.find(
		(p) => p.mergeRequestEventType === "MERGED_RESULT",
	);
	const latest = allPipelines.find((p) => p.latest === true);

	// If we have both MERGED_RESULT and latest, merge them
	if (mergedResult && latest) {
		return mergeTwoPipelines(mergedResult, latest);
	}

	// If only MERGED_RESULT exists, return it
	if (mergedResult) {
		return mergedResult;
	}

	// If only latest exists, return it
	if (latest) {
		return latest;
	}

	// If none of the above, check for null mergeRequestEventType
	const nullTypePipelines = allPipelines.filter(
		(p) => p.mergeRequestEventType === null,
	);

	if (nullTypePipelines.length >= 2) {
		// If multiple nullType pipelines, return the one with latest=true
		const latestNullType = nullTypePipelines.find((p) => p.latest === true);
		return latestNullType || null;
	}
	if (nullTypePipelines.length === 1) {
		// If only one nullType pipeline, return it
		return nullTypePipelines[0];
	}
	// If all else fails, return null
	return null;
};

/**
 * Merges two pipelines according to rules
 * @param {Object} pipeline1 - First pipeline
 * @param {Object} pipeline2 - Second pipeline
 * @returns {Object} - Merged pipeline
 */
function mergeTwoPipelines(pipeline1, pipeline2) {
	if (!pipeline1) return pipeline2;
	if (!pipeline2) return pipeline1;
	if (pipeline1.id === pipeline2.id) return pipeline1;

	// Define status priority (higher index = worse status)
	const statusPriority = [
		"CREATED",
		"SCHEDULED",
		"WAITING_FOR_RESOURCE",
		"PREPARING",
		"WAITING_FOR_CALLBACK",
		"PENDING",
		"RUNNING",
		"MANUAL",
		"SKIPPED",
		"SUCCESS",
		"CANCELING",
		"CANCELED",
		"FAILED",
	];

	// Get status priority
	const status1Priority = statusPriority.indexOf(pipeline1.status);
	const status2Priority = statusPriority.indexOf(pipeline2.status);

	// Choose the worst status (highest priority index)
	const worstStatus =
		status1Priority >= status2Priority ? pipeline1.status : pipeline2.status;

	// Combine job counts
	const totalJobs = {
		count:
			(pipeline1.totalJobs?.count || 0) + (pipeline2.totalJobs?.count || 0),
	};

	const failedJobs = {
		count:
			(pipeline1.failedJobs?.count || 0) + (pipeline2.failedJobs?.count || 0),
	};

	const passedJobs = {
		count:
			(pipeline1.passedJobs?.count || 0) + (pipeline2.passedJobs?.count || 0),
	};

	return {
		...pipeline1,
		status: worstStatus,
		totalJobs,
		failedJobs,
		passedJobs,
	};
}
